import React, { useState, useEffect } from 'react';
import { getAllInvitations, createInvitation, deleteInvitation } from '../api/invitationApi';
import { format } from 'date-fns';
import { Trash2, RefreshCw, Copy, Check } from 'lucide-react';
import logger from '../utils/logger';


const InvitationList = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resendingId, setResendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getAllInvitations();
      logger.debug('Invitations data received:', data);
      
      // Ensure we have valid data
      if (Array.isArray(data)) {
        setInvitations(data);
      } else {
        logger.error('Invalid invitation data format:', data);
        setError('Received invalid data format from server');
        setInvitations([]);
      }
    } catch (error) {
      logger.error('Error fetching invitations:', error);
      setError(error.response?.data?.message || 'Failed to load invitations');
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (invitation) => {
    setResendingId(invitation._id);
    setError('');
    
    try {
      const response = await createInvitation({
        email: invitation.email,
        name: invitation.name
      });
      
      logger.debug('Resend invitation response:', response);
      
      // Update the invitation in the list
      if (response.invitation) {
        setInvitations(prev => 
          prev.map(inv => 
            inv._id === invitation._id ? response.invitation : inv
          )
        );
        
        // Show success message
        alert(`Invitation resent to ${invitation.email}`);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      logger.error('Error resending invitation:', error);
      setError(error.response?.data?.message || 'Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const handleDeleteInvitation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invitation?')) {
      return;
    }
    
    setDeletingId(id);
    setError('');
    
    try {
      await deleteInvitation(id);
      
      // Remove the invitation from the list
      setInvitations(prev => prev.filter(inv => inv._id !== id));
    } catch (error) {
      logger.error('Error deleting invitation:', error);
      setError(error.response?.data?.message || 'Failed to delete invitation');
    } finally {
      setDeletingId(null);
    }
  };

  const copyCodeToClipboard = (code) => {
    if (!code) {
      alert('No code available to copy');
      return;
    }
    
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (error) {
      logger.error('Error formatting date:', dateString, error);
      return 'Invalid date';
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Safely sort invitations with error handling
  const sortedInvitations = React.useMemo(() => {
    try {
      return [...invitations].sort((a, b) => {
        let comparison = 0;
        
        if (!a || !b) return 0;
        
        if (sortField === 'email' || sortField === 'name' || sortField === 'status' || sortField === 'code') {
          comparison = String(a[sortField] || '').localeCompare(String(b[sortField] || ''));
        } else if (sortField === 'createdAt' || sortField === 'expiresAt') {
          const dateA = a[sortField] ? new Date(a[sortField]) : new Date(0);
          const dateB = b[sortField] ? new Date(b[sortField]) : new Date(0);
          comparison = dateA - dateB;
        }
        
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    } catch (error) {
      logger.error('Error sorting invitations:', error);
      return invitations;
    }
  }, [invitations, sortField, sortDirection]);

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="p-4 bg-white rounded shadow">
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-white rounded shadow">
        <div className="text-red-500 mb-4">{error}</div>
        <button 
          onClick={fetchInvitations}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800">Sent Invitations</h2>
        <p className="text-sm text-gray-600">Total: {invitations.length} invitations</p>
        {invitations.length === 0 && !loading && !error && (
          <p className="text-sm text-gray-500 mt-2">
            No invitations found. Generate an invitation URL to get started.
          </p>
        )}
      </div>
      
      {invitations.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('name')}
                >
                  Name {getSortIcon('name')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('email')}
                >
                  Email {getSortIcon('email')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('code')}
                >
                  Code {getSortIcon('code')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('createdAt')}
                >
                  Sent Date {getSortIcon('createdAt')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('expiresAt')}
                >
                  Expires {getSortIcon('expiresAt')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('status')}
                >
                  Status {getSortIcon('status')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedInvitations.map((invitation) => (
                <tr key={invitation._id || Math.random().toString()} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invitation.name || 'N/A'}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{invitation.email || 'N/A'}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 flex items-center">
                      <span className="font-mono">{invitation.code || 'N/A'}</span>
                      {invitation.code && (
                        <button 
                          onClick={() => copyCodeToClipboard(invitation.code)}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                          title="Copy code"
                        >
                          {copiedCode === invitation.code ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{formatDate(invitation.createdAt)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{formatDate(invitation.expiresAt)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      invitation.status === 'pending' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {invitation.status === 'pending' ? 'Pending' : 'Used'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {invitation.status === 'pending' ? (
                      <>
                        <button
                          onClick={() => handleResendInvitation(invitation)}
                          disabled={resendingId === invitation._id}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                          title="Resend invitation"
                        >
                          {resendingId === invitation._id ? (
                            <RefreshCw size={16} className="animate-spin" />
                          ) : (
                            <RefreshCw size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteInvitation(invitation._id)}
                          disabled={deletingId === invitation._id}
                          className="text-red-600 hover:text-red-900"
                          title="Delete invitation"
                        >
                          {deletingId === invitation._id ? (
                            <Trash2 size={16} className="animate-pulse" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </>
                    ) : (
                      <span className="text-gray-400">Used</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvitationList;
